import React from "react";
import {
  API_get_learning_track_enrollment_summaries_for_employee,
  API_get_learning_track_enrollment_detail_for_employee,
  API_post_learning_section_completed,
  API_post_learning_block_user_passed,
} from "../../../constants";
import LoadSpinner from "../../../utilities/LoadSpinner";
import { getRightworkAPI } from "../requests/utilities/requests";
import { postRightworkAPI } from "../requests/utilities/requests";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import TopBarTakeTraining from "../home/top_bar_components/TopBarTakeTraining";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import CheckIcon from "@mui/icons-material/Check";
import CompleteTrack from "./CompleteTrack";
import SchoolIcon from "@mui/icons-material/School";
import LanguageIcon from "@mui/icons-material/Language";
import ProgressIcon from "@mui/icons-material/TrendingUp";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { IconButton, Menu, MenuItem, Tooltip } from '@mui/material';

const TRANSLATIONS = {
  title: {
    english: "My Training",
    spanish: "Mi Capacitación",
    chinese: "我的培训",
  },
  subtitle: {
    english: "Continue your professional development with personalized learning tracks",
    spanish: "Continúa tu desarrollo profesional con cursos personalizados",
    chinese: "通过个性化学习路径继续提升你的职业技能",
  },
  enrolledTracks: {
    english: "Enrolled Tracks",
    spanish: "Rutas Inscritas",
    chinese: "已报名课程",
  },
  averageProgress: {
    english: "Average Progress",
    spanish: "Progreso Promedio",
    chinese: "平均进度",
  },
  language: {
    english: "Language",
    spanish: "Idioma",
    chinese: "语言",
  },
  overdue: {
    english: "Overdue:",
    spanish: "Vencido:",
    chinese: "已逾期：",
  },
  due: {
    english: "Due:",
    spanish: "Fecha límite:",
    chinese: "截止日期：",
  },
  of: {
    english: "of",
    spanish: "de",
    chinese: "共",
  },
  sections: {
    english: "sections",
    spanish: "secciones",
    chinese: "个章节",
  },
  completed: {
    english: "completed",
    spanish: "completado",
    chinese: "已完成",
  },
  completedButton: {
    english: "Completed",
    spanish: "Completado",
    chinese: "已完成",
  },
  begin: {
    english: "Begin",
    spanish: "Comenzar",
    chinese: "开始",
  },
  continue: {
    english: "Continue",
    spanish: "Continuar",
    chinese: "继续",
  },
  upNext: {
    english: "Up next:",
    spanish: "Siguiente:",
    chinese: "下一项：",
  },
  module: {
    english: "Module",
    spanish: "Módulo",
    chinese: "模块",
  },
  section: {
    english: "Section",
    spanish: "Sección",
    chinese: "章节",
  },
  congratulations: {
    english: "Congratulations!",
    spanish: "¡Felicidades!",
    chinese: "恭喜！",
  },
  completedTrackMessage: {
    english: "You have completed all modules and sections in this track",
    spanish: "Has completado todos los módulos y secciones de esta ruta",
    chinese: "你已完成此学习路径中的所有模块和章节",
  },
  noTracksAssigned: {
    english: "No tracks assigned",
    spanish: "No hay rutas asignadas",
    chinese: "暂无分配的课程",
  },
  noTracksDescription: {
    english: "You are not currently enrolled in any training tracks. Contact your supervisor or administrator for more information.",
    spanish: "No estás inscrito en ninguna ruta de capacitación actualmente. Contacta a tu supervisor o administrador para más información.",
    chinese: "你当前还没有报名任何培训课程。如需更多信息，请联系你的主管或管理员。",
  },
  active: {
    english: "Active",
    spanish: "Activos",
    chinese: "进行中",
  },
  noActiveTracks: {
    english: "No active tracks",
    spanish: "No hay rutas activas",
    chinese: "暂无进行中的课程",
  },
  noActiveTracksDescription: {
    english: "You're all caught up. Check the Completed tab to revisit finished tracks.",
    spanish: "Estás al día. Revisa la pestaña Completados para volver a ver las rutas terminadas.",
    chinese: "你已完成所有任务。可在“已完成”标签中查看已结束的课程。",
  },
  noCompletedTracks: {
    english: "No completed tracks yet",
    spanish: "Aún no hay rutas completadas",
    chinese: "暂无已完成的课程",
  },
  noCompletedTracksDescription: {
    english: "Finish a track to see it here.",
    spanish: "Completa una ruta para verla aquí.",
    chinese: "完成一项课程后将在此显示。",
  },
  trackCompleted: {
    english: "Track completed",
    spanish: "Ruta completada",
    chinese: "课程已完成",
  },
};

const LANGUAGE_LOCALES = {
  english: "en-US",
  spanish: "es-ES",
  chinese: "zh-CN",
};

function TakeTrainingEmployeeDisplay(props) {
  const [enrolledTracks, setEnrolledTracks] = React.useState([]);

  const [modalDataTrack, setModalDataTrack] = React.useState([]);

  const [currentTrack, setCurrentTrack] = React.useState(null);
  const [currentTrackUUID, setCurrentTrackUUID] = React.useState(null);

  const [isLoading, setIsLoading] = React.useState(false);

  const [showCompleteTrack, setShowCompleteTrack] = React.useState(false);

  // Holds the UUID we'd like to deep-link into via the mobile hand-off, until
  // currentTrack catches up from the summary fetch. CompleteTrack reads its
  // initial selected module/section from currentTrack on first render, so we
  // must not flip showCompleteTrack until currentTrack is populated, otherwise
  // the player mounts on a blank state.
  //
  // Initialized synchronously from props.pendingTrackUuid so the very first
  // render of this component (the one that happens before any effect fires)
  // already knows we're mid-deep-link. Without that, the list would render
  // empty (since fetches haven't started yet) and "No tracks assigned" would
  // flash, then the full list would flash, before CompleteTrack mounts.
  const [pendingOpenUuid, setPendingOpenUuid] = React.useState(
    props.pendingTrackUuid || null
  );

  // Tracks whether the enrolledTracks summary fetch has resolved at least
  // once (success OR failure). Used by the deep-link continuation effect so
  // a stale / no-longer-enrolled trackUuid can give up and fall through to
  // the list rather than sit on a permanent spinner. Also doubles as the
  // signal the pending effect reads to know whether the mount-time fetch
  // has already happened.
  const [enrolledTracksFetchAttempted, setEnrolledTracksFetchAttempted] =
    React.useState(false);

  const [language, setLanguage] = React.useState(() => {
    // Try to get the saved language from localStorage
    const savedLanguage = localStorage.getItem('preferredLanguage');
    // Return the saved language or default to "English" if nothing is saved
    return savedLanguage || "english";
  });

  const [languageAnchorEl, setLanguageAnchorEl] = React.useState(null);
  const handleLanguageButtonClick = (event) => setLanguageAnchorEl(event.currentTarget);
  const handleLanguageMenuClose = () => setLanguageAnchorEl(null);
  const selectLanguage = (newLanguage) => {
    setLanguage(newLanguage);
    localStorage.setItem('preferredLanguage', newLanguage);
    handleLanguageMenuClose();
  };

  const [activeTab, setActiveTab] = React.useState("active");

  const default_data_section = {
    created_at: null,
    name: "",
    updated_at: null,
    uuid: null,
    as_json: [],
  };
  const [modalDataSection, setModalDataSection] =
    React.useState(default_data_section);

  const fetchEnrolledTracks = async (showLoading) => {
    if (!props.homeCompany) {
      console.warn("fetchEnrolledTracks called without homeCompany");
      return;
    }
    setIsLoading(showLoading);
    let api_params = {
      company_id: props.homeCompany,
      language: language
    };
    let api = API_get_learning_track_enrollment_summaries_for_employee;
    try {
      const res = await getRightworkAPI(api, api_params);
      // console.log(res);
      if (res.status === 200) {
        const data = res.data?.redux?.payload?.learning_track_enrollments;
        // Fall back to [] so a missing/malformed payload can't make
        // enrolledTracks become undefined — downstream code (.find(), .filter(),
        // .reduce()) all expect an array and would throw otherwise.
        setEnrolledTracks(data || []);
      }
    } catch (error) {
      throw Error(error);
    } finally {
      // Clear the spinner regardless of outcome — otherwise a failed fetch
      // leaves the page stuck on the loading state. Also signals to the
      // deep-link continuation effect that we've attempted to load the
      // summary so it can give up on stale UUIDs.
      setIsLoading(false);
      setEnrolledTracksFetchAttempted(true);
    }
  };

  const fetchTrackDetails = async (showLoading, learning_track_uuid) => {
    if (!props.homeCompany) {
      console.warn("fetchTrackDetails called without homeCompany");
      return;
    }
    setIsLoading(showLoading);
    let api_params = {
      company_id: props.homeCompany,
      learning_track_uuid: learning_track_uuid,
      language: language
    };
    let api = API_get_learning_track_enrollment_detail_for_employee;
    try {
      const res = await getRightworkAPI(api, api_params);
      if (res.status === 200) {
        setModalDataTrack(res.data.redux?.payload.learning_track);
      }
    } catch (error) {
      console.error("Error fetching track details:", error);
      setModalDataTrack([]);
    } finally {
      // Same rationale as fetchEnrolledTracks — ensure the spinner clears
      // even when the request fails.
      setIsLoading(false);
    }
  };

  React.useEffect(() => {
    if (!showCompleteTrack && props.homeCompany) {
      fetchEnrolledTracks(true);
    }
  }, [showCompleteTrack, language, props.homeCompany]);

  // Mobile WebView hand-off: when the native app drives us to a specific
  // track via the mobile_to_web_session redirect, kick off both fetches and
  // remember which UUID we want to open. The actual showCompleteTrack flip
  // happens in the continuation effect below, once currentTrack is populated
  // from the enrolledTracks summary fetch — flipping it before that lands
  // would mount CompleteTrack with currentTrack=null and leave the player on
  // a blank/uninitialized state in slow-network or race conditions.
  //
  // Each deep-link arrival also resets enrolledTracksFetchAttempted and
  // refetches the summary list. Without that reset, a subsequent
  // pendingTrackUuid in the same session would be evaluated against the
  // previously-loaded (possibly stale) enrolledTracks: if the user got
  // enrolled in the deep-linked track AFTER the initial fetch — or the
  // initial fetch failed entirely — the continuation effect would find no
  // match, see attempted=true, and prematurely drop the hand-off back to
  // the list view.
  React.useEffect(() => {
    if (!props.pendingTrackUuid || !props.homeCompany) {
      return;
    }
    // Capture BEFORE we reset the flag below — if a prior fetch already
    // landed (success or failure) in this session, the flag is currently
    // true and this is a follow-up deep-link that needs fresh data. If
    // the flag is still false, we're in the initial-load path (whether
    // homeCompany was truthy on first render or only became truthy now)
    // and the mount-time effect already kicked off the fetch — firing a
    // duplicate here would put two summary requests in flight whose
    // finallies race on enrolledTracksFetchAttempted, and a faster
    // failed/stale response could prematurely trigger the continuation
    // effect's give-up branch.
    const summaryAlreadyFetchedThisSession = enrolledTracksFetchAttempted;
    setCurrentTrackUUID(props.pendingTrackUuid);
    setPendingOpenUuid(props.pendingTrackUuid);
    setEnrolledTracksFetchAttempted(false);
    if (summaryAlreadyFetchedThisSession) {
      fetchEnrolledTracks(false);
    }
    fetchTrackDetails(true, props.pendingTrackUuid);
    if (typeof props.onConsumePendingTrackUuid === "function") {
      props.onConsumePendingTrackUuid();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.pendingTrackUuid, props.homeCompany]);

  // Continuation of the mobile hand-off:
  //   - Happy path: enrolledTracks now contains a match for the deep-link
  //     UUID → write currentTrack to the match AND surface CompleteTrack
  //     in the same batch, so the player mounts already pointing at the
  //     right track.
  //   - Give-up path: the enrolledTracks fetch has resolved (success or
  //     failure) but no enrollment matches the deep-link UUID (stale link,
  //     user unenrolled, fetch error). Clear pendingOpenUuid so the render
  //     guard releases and the list view (or empty state) takes over —
  //     otherwise the page would sit on a permanent spinner.
  //
  // Match check is done inline against enrolledTracks rather than via
  // currentTrack. currentTrack is set by a separate effect that also
  // depends on enrolledTracks, and effects run in declaration order with
  // their pre-update closure values — so checking currentTrack here would
  // race with the find effect and prematurely fall back to the list even
  // when the user is enrolled.
  //
  // setCurrentTrack(match) is also fired from this effect rather than
  // relying on the find effect, because for late deep-links the find
  // effect only refires when enrolledTracks changes — so a failed refetch
  // (or any path where enrolledTracks stays stable across deep-link calls)
  // would leave currentTrack pointing at the previous track when we mount
  // CompleteTrack. Calling setCurrentTrack here batches the assignment
  // into the same render as setShowCompleteTrack.
  React.useEffect(() => {
    if (!pendingOpenUuid) return;
    const match = (enrolledTracks || []).find(
      (t) => t?.track?.uuid === pendingOpenUuid
    );
    if (match) {
      setCurrentTrack(match);
      setShowCompleteTrack(true);
      setPendingOpenUuid(null);
      return;
    }
    if (enrolledTracksFetchAttempted) {
      setPendingOpenUuid(null);
    }
  }, [enrolledTracks, pendingOpenUuid, enrolledTracksFetchAttempted]);

  // ----------- POST APIs ----------- //

  // [POST] api/lms_info/post_learning_section_completed
  // * company_id
  // * learning_section_uuid

  const post_learning_section_completed = async (learning_section_uuid) => {
    if (!props.homeCompany) {
      console.warn("post_learning_section_completed called without homeCompany");
      toast.error("Unable to complete section: missing company information", { autoClose: 3000, position: "top-center" });
      return false;
    }
    // Prepare data to send
    const requestData = {
      company_id: props.homeCompany,
      learning_section_uuid: learning_section_uuid,
    };

    // No loading/success toasts: the player's Continue button completes and
    // advances in one tap, so moving to the next section is the confirmation.
    // Returns whether the completion saved so the player only advances then.
    try {
      const res = await postRightworkAPI(
        API_post_learning_section_completed,
        requestData
      );

      if (res.status === 200) {
        fetchEnrolledTracks(false);
        fetchTrackDetails(false, currentTrackUUID);
        return true;
      }
      return false;
    } catch (error) {
      // Hide the toast's own close (X) on MOBILE ONLY: there, the native
      // WebView close button sits above the WebView at the same top-right
      // corner. `props.embedded` is true only in the mobile WebView.
      toast.error(error.response?.data?.system?.message || "Request Failed", {
        autoClose: 3000,
        position: "top-center",
        closeButton: !props.embedded,
      });
      console.error("Error:", error);
      return false;
    }
  };

  // [POST] api/lms_info/post_learning_block_user_passed
  // * company_id
  // * learning_quiz_uuid

  const post_learning_block_user_passed = async (
    learning_block_uuid,
    is_passed,
    employee_id = null,
    score = null,
    question_responses = null,
    options = {}
  ) => {
    if (!props.homeCompany) {
      console.warn("post_learning_block_user_passed called without homeCompany");
      return;
    }
    // Prepare data to send
    const requestData = {
      company_id: props.homeCompany,
      learning_block_uuid: learning_block_uuid,
      is_passed: is_passed,
    };

    // Add optional parameters if provided
    if (employee_id !== null) {
      requestData.store_team_member_id = employee_id;
    }
    if (score !== null) {
      requestData.score = score;
    }
    if (question_responses !== null && question_responses.length > 0) {
      requestData.question_responses = JSON.stringify(question_responses);
    }

    try {
      // toast.loading("Checking Quiz Score...", { autoClose: false });

      const res = await postRightworkAPI(
        API_post_learning_block_user_passed,
        requestData
      );

      if (res.status === 200) {
        // toast.dismiss();
        // toast.success("Quiz Passed Successfully", { autoClose: 3000 });

        // Awaited so Continue knows which sections a test-out pass just covered
        // by the time the learner can tap it.
        await fetchTrackDetails(false, currentTrackUUID);

        // Return the response data including attempt_uuid if present
        return res.data.redux?.payload;
      }
    } catch (error) {
      toast.dismiss();
      console.error("Error:", error);
      const systemMessage = error.response?.data?.system?.message || null;
      // Fire a user-visible failure toast by default so fire-and-forget
      // callers (Quiz.js / Checkpoint.js manager checkboxes set isChecked
      // optimistically and never await) don't silently leave the UI in a
      // false "saved" state. Awaiting callers (CompleteQuizModal) pass
      // { silent: true } so they can branch on the returned error_message
      // — e.g. show a green "You already passed this quiz" instead of a
      // red error — without a red-flash-then-green glitch.
      if (!options.silent) {
        toast.error(systemMessage || "Request Failed", { autoClose: 3000 });
      }
      // Surface the backend's explanation (if any) up to the caller so it
      // can pick a sensible toast. Returning an object (not undefined)
      // lets callers branch on `error_message`.
      return {
        error_message: systemMessage,
      };
    }
  };

  // console.log("DATA", enrolledTracks)
  // console.log("DATA2", modalDataTrack)

  // console.log("CURRENT TRACK", currentTrack)
  // console.log("ENROLLED TRACKS", enrolledTracks)

  const userType =
    props.permissionRole === "company_employee" ? "Employee" : "Manager";

  const t = React.useCallback((key) => {
    return TRANSLATIONS[key]?.[language] || TRANSLATIONS[key]?.english || key;
  }, [language]);

  const dateLocale = LANGUAGE_LOCALES[language] || LANGUAGE_LOCALES.english;

  React.useEffect(() => {
    const updatedTrack = enrolledTracks.find(
      (track) => track.track.uuid === currentTrackUUID
    );
    setCurrentTrack(updatedTrack);
  }, [enrolledTracks]);

  const getTrackProgress = React.useCallback((track) => {
    if (!track) return 0;
    if (!track.total_sections_count || track.total_sections_count <= 0) {
      return track.completed_at ? 100 : 0;
    }
    return (track.sections_completed_count / track.total_sections_count) * 100;
  }, []);

  const isTrackComplete = React.useCallback((track) => {
    return Boolean(track?.completed_at) || getTrackProgress(track) === 100;
  }, [getTrackProgress]);

  const activeTracks = React.useMemo(() => {
    return [...enrolledTracks]
      .filter((t) => !isTrackComplete(t))
      .sort((a, b) => {
        const aDueDate = a?.effective_due_date;
        const bDueDate = b?.effective_due_date;
        const aHasDueDate = Boolean(aDueDate);
        const bHasDueDate = Boolean(bDueDate);

        if (aHasDueDate !== bHasDueDate) {
          return aHasDueDate ? -1 : 1;
        }
        if (aHasDueDate && bHasDueDate && aDueDate !== bDueDate) {
          return aDueDate - bDueDate;
        }

        const aProgress = getTrackProgress(a);
        const bProgress = getTrackProgress(b);
        if (aProgress !== bProgress) {
          return bProgress - aProgress;
        }

        return (a?.track?.name || "").localeCompare(b?.track?.name || "");
      });
  }, [enrolledTracks, getTrackProgress, isTrackComplete]);

  const completedTracks = React.useMemo(() => {
    return [...enrolledTracks]
      .filter(isTrackComplete)
      .sort((a, b) => (b?.completed_at || 0) - (a?.completed_at || 0));
  }, [enrolledTracks, isTrackComplete]);

  const visibleTracks = activeTab === "completed" ? completedTracks : activeTracks;

  return (
    <>
      {(!isLoading && !pendingOpenUuid) ? (
        <div>
          {!showCompleteTrack ? (
            <div className="w-full min-h-screen flex flex-col">
              {!props.embedded && (
                <div className="w-full md:hidden">
                  <TopBarTakeTraining title="Training" />
                </div>
              )}
              <div className="w-full flex-1 bg-white">
                {/* Header Section */}
                <div className="w-full bg-white border-b border-gray-200">
                  <div className="px-6 md:px-4 py-8">
                    <div className="flex items-start justify-between gap-x-6 mb-6">
                      <div className="min-w-0">
                        <h1 className="text-2xl font-bold text-slate-900 mb-1">
                          {t("title")}
                        </h1>
                        <p className="text-sm text-slate-600">
                          {t("subtitle")}
                        </p>
                      </div>
                      {!props.embedded && (
                        <>
                          <Tooltip title={t("language")}>
                            <IconButton
                              onClick={handleLanguageButtonClick}
                              size="small"
                              sx={{
                                border: "1px solid #e2e8f0",
                                borderRadius: "8px",
                                padding: "8px",
                                color: "#475569",
                                "&:hover": {
                                  backgroundColor: "#f8fafc",
                                  borderColor: "#cbd5e1",
                                },
                              }}
                            >
                              <LanguageIcon style={{ fontSize: "20px" }} />
                            </IconButton>
                          </Tooltip>
                          <Menu
                            anchorEl={languageAnchorEl}
                            open={Boolean(languageAnchorEl)}
                            onClose={handleLanguageMenuClose}
                            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                            transformOrigin={{ vertical: "top", horizontal: "right" }}
                          >
                            <MenuItem selected={language === "english"} onClick={() => selectLanguage("english")}>English</MenuItem>
                            <MenuItem selected={language === "spanish"} onClick={() => selectLanguage("spanish")}>Español</MenuItem>
                            <MenuItem selected={language === "chinese"} onClick={() => selectLanguage("chinese")}>中文</MenuItem>
                          </Menu>
                        </>
                      )}
                    </div>

                    {/* Stats Row */}
                    <div className="flex items-center gap-6 text-sm text-slate-600">
                      <div className="flex items-center gap-2">
                        <SchoolIcon style={{ fontSize: "18px" }} />
                        <span>
                          {enrolledTracks.length} {t("enrolledTracks")}
                        </span>
                      </div>
                      {enrolledTracks.length > 0 && (
                        <div className="flex items-center gap-2">
                          <ProgressIcon style={{ fontSize: "18px" }} />
                          <span>
                            {Math.round(
                              enrolledTracks.reduce((acc, track) => {
                                return acc + getTrackProgress(track);
                              }, 0) / enrolledTracks.length
                            )}% {t("averageProgress")}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Tabs */}
                    {enrolledTracks.length > 0 && (
                      <div className="mt-6 flex items-center gap-1 border-b border-gray-200 -mb-8">
                        {[
                          { key: "active", label: t("active"), count: activeTracks.length },
                          { key: "completed", label: t("completedButton"), count: completedTracks.length },
                        ].map((tab) => {
                          const isActive = activeTab === tab.key;
                          return (
                            <button
                              key={tab.key}
                              onClick={() => setActiveTab(tab.key)}
                              className={`relative px-4 py-3 text-sm font-medium transition-colors ${
                                isActive
                                  ? "text-violet-700"
                                  : "text-slate-500 hover:text-slate-700"
                              }`}
                            >
                              <span>{tab.label}</span>
                              <span
                                className={`ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-xs ${
                                  isActive
                                    ? "bg-violet-100 text-violet-700"
                                    : "bg-slate-100 text-slate-600"
                                }`}
                              >
                                {tab.count}
                              </span>
                              {isActive && (
                                <span className="absolute left-0 right-0 -bottom-px h-0.5 bg-violet-700 rounded-full" />
                              )}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>

                {/* Content */}
                <div className="px-6 md:px-4 py-8">
                  {enrolledTracks.length === 0 ? (
                    <div className="text-center py-16">
                      <div className="w-16 h-16 bg-slate-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                        <SchoolIcon style={{ fontSize: "32px", color: "#64748b" }} />
                      </div>
                      <h3 className="text-xl font-medium text-gray-900 mb-2">
                        {t("noTracksAssigned")}
                      </h3>
                      <p className="text-gray-500 max-w-md mx-auto">
                        {t("noTracksDescription")}
                      </p>
                    </div>
                  ) : visibleTracks.length === 0 ? (
                    <div className="text-center py-16">
                      <div className="w-16 h-16 bg-slate-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                        <SchoolIcon style={{ fontSize: "32px", color: "#64748b" }} />
                      </div>
                      <h3 className="text-xl font-medium text-gray-900 mb-2">
                        {activeTab === "completed" ? t("noCompletedTracks") : t("noActiveTracks")}
                      </h3>
                      <p className="text-gray-500 max-w-md mx-auto">
                        {activeTab === "completed" ? t("noCompletedTracksDescription") : t("noActiveTracksDescription")}
                      </p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-3 xl:grid-cols-2 md:grid-cols-1 gap-6">
                      {visibleTracks.map((track, index) => {
                        const progressPct = getTrackProgress(track);
                        const percentCompleted = Math.round(progressPct);
                        const isCompleted = isTrackComplete(track);
                        const isOverdue = track.effective_due_date && !isCompleted && (() => {
                          const now = new Date();
                          const todayUTC = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
                          return track.effective_due_date < todayUTC;
                        })();
                        const heroFallbackColor = track?.next_module?.color || "bg-slate-400";
                        const coverUrl = track?.track?.cover?.url;

                        return (
                          <div
                            key={index}
                            onClick={() => {
                              setCurrentTrack(track);
                              setCurrentTrackUUID(track.track.uuid);
                              fetchTrackDetails(true, track.track.uuid);
                              setShowCompleteTrack(true);
                            }}
                            className="bg-white rounded-xl border border-slate-200 hover:border-slate-300 hover:shadow-lg transition-all duration-200 overflow-hidden cursor-pointer group flex flex-col"
                          >
                            {/* Hero */}
                            <div className="relative aspect-[16/9] w-full overflow-hidden bg-slate-100">
                              <div className={`absolute inset-0 ${heroFallbackColor}`} />
                              {coverUrl && (
                                <img
                                  src={coverUrl}
                                  alt={track.track.name}
                                  className="absolute inset-0 w-full h-full object-cover"
                                  draggable={false}
                                  onError={(e) => {
                                    e.currentTarget.style.display = "none";
                                  }}
                                />
                              )}
                              {/* Bottom scrim for legibility */}
                              <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/75 via-black/35 to-transparent" />

                              {/* Due-date pill */}
                              {track.effective_due_date && !isCompleted && (
                                <div className={`absolute top-3 right-3 flex items-center gap-1 px-2 py-1 rounded-md backdrop-blur-sm whitespace-nowrap ${
                                  isOverdue
                                    ? 'bg-red-500/90 text-white'
                                    : 'bg-amber-400/95 text-amber-950'
                                }`}>
                                  <CalendarTodayIcon style={{ fontSize: "12px" }} />
                                  <span className="text-xs font-semibold">
                                    {isOverdue ? t("overdue") : t("due")}{" "}
                                    {new Date(track.effective_due_date).toLocaleDateString(
                                      dateLocale,
                                      {
                                        month: 'short',
                                        day: 'numeric',
                                        timeZone: 'UTC',
                                      }
                                    )}
                                  </span>
                                </div>
                              )}

                              {/* Completed badge */}
                              {isCompleted && (
                                <div className="absolute top-3 right-3 flex items-center gap-1 px-2 py-1 rounded-md bg-emerald-500/95 text-white whitespace-nowrap">
                                  <CheckIcon style={{ fontSize: "14px" }} />
                                  <span className="text-xs font-semibold">{t("completedButton")}</span>
                                </div>
                              )}

                              {/* Title + progress */}
                              <div className="absolute inset-x-0 bottom-0 p-4 text-white">
                                <h3 className="text-lg font-semibold line-clamp-2 drop-shadow-sm mb-2">
                                  {track.track.name}
                                </h3>
                                <div className="flex items-center gap-3">
                                  <div className="flex-1 bg-white/25 rounded-full h-1.5 overflow-hidden">
                                    <div
                                      className={`h-full rounded-full transition-all duration-500 ${
                                        isCompleted ? "bg-emerald-400" : "bg-white"
                                      }`}
                                      style={{ width: `${percentCompleted}%` }}
                                    />
                                  </div>
                                  <span className="text-xs font-semibold tabular-nums">
                                    {percentCompleted}%
                                  </span>
                                </div>
                              </div>
                            </div>

                            {/* Body */}
                            <div className="p-4 flex flex-col flex-1 gap-3">
                              <div className="flex items-center justify-between text-xs text-slate-500">
                                <span>
                                  {track.sections_completed_count} {t("of")} {track.total_sections_count} {t("sections")}
                                </span>
                              </div>

                              {!isCompleted && track.next_module && track.next_section ? (
                                <div className="flex-1 min-h-0">
                                  <div className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">
                                    {t("upNext")}
                                  </div>
                                  <div className="text-sm text-slate-800 line-clamp-2">
                                    <span className="font-medium">
                                      {t("module")} {track.next_module_index + 1}:
                                    </span>{" "}
                                    {track.next_module.name}
                                    <span className="text-slate-400"> · </span>
                                    {track.next_section.name}
                                  </div>
                                </div>
                              ) : (
                                <div className="flex-1 min-h-0 flex items-center gap-2 text-sm text-emerald-700">
                                  <CheckIcon style={{ fontSize: "16px" }} />
                                  <span>{t("trackCompleted")}</span>
                                </div>
                              )}

                              <button
                                className={`w-full px-4 py-2.5 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
                                  isCompleted
                                    ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                                    : "bg-violet-700 hover:bg-violet-800 text-white"
                                }`}
                              >
                                {isCompleted ? (
                                  <>
                                    <CheckIcon style={{ fontSize: "18px" }} />
                                    <span>{t("completedButton")}</span>
                                  </>
                                ) : (
                                  <>
                                    <PlayArrowIcon style={{ fontSize: "18px" }} />
                                    <span>{percentCompleted === 0 ? t("begin") : t("continue")}</span>
                                  </>
                                )}
                                <KeyboardArrowRightIcon
                                  style={{ fontSize: "18px" }}
                                  className="group-hover:translate-x-1 transition-transform duration-200"
                                />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="w-full min-h-screen">
              <CompleteTrack
                currentTrack={currentTrack}
                modalDataTrack={modalDataTrack}
                setModalDataTrack={setModalDataTrack}
                modalDataSection={modalDataSection}
                setModalDataSection={setModalDataSection}
                homeCompany={props.homeCompany}
                showCompleteTrack={showCompleteTrack}
                setShowCompleteTrack={setShowCompleteTrack}
                post_learning_section_completed={
                  post_learning_section_completed
                }
                post_learning_block_user_passed={
                  post_learning_block_user_passed
                }
                userType={userType}
                language={language}
                embedded={props.embedded}
              />
            </div>
          )}
        </div>
      ) : (
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
        >
          <LoadSpinner />
        </div>
      )}
    </>
  );
}

export default TakeTrainingEmployeeDisplay;
